import $ from 'jquery';
import EsriJSON from 'ol/format/EsriJSON';
import { Vector } from "ol/layer";
import { tile as tileStrategy } from 'ol/loadingstrategy.js';
import VectorSource from "ol/source/Vector";
import { Fill, Icon, RegularShape, Stroke, Style, Text } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import { createXYZ } from 'ol/tilegrid.js';
import { ServerLayer } from "../../../lib/layers";
import { Some } from "../../../lib/misc/option";
import TextPlacement from 'ol/style/TextPlacement';
export class VectorLayer extends ServerLayer {
    constructor(opts) {
        super(opts);
        this.legend_ui = $();
        this.legend_entries = [];
        this.styles = new Map();
    }
    get layers() {
        return this.raw.map(l => [l]).unwrapOr([]);
    }
    validate(opts) {
        let result = true;
        result && (result = Array.isArray(opts.exportUrl) && opts.exportUrl.every(u => typeof u === 'string'));
        result && (result = typeof opts.isBaseLayer === 'boolean' && typeof opts.visible === 'boolean');
        return result;
    }
    async parse_config(opts) {
        var _a, _b, _c, _d;
        this.is_base = opts.isBaseLayer;
        this.name = opts.name;
        const serviceUrl = opts.exportUrl[0];
        const drawingInfo = await this.get_vector_layer_info(serviceUrl);
        if (!drawingInfo) {
            console.error(`Vector layer "${opts.name}" loading failed. See console logs.`);
            this.parsing_failed = true;
            return;
        }
        ;
        this.extract_styles(drawingInfo);
        const vectorSource = new VectorSource({
            format: new EsriJSON(),
            url: (extent, _resolution, projection) => {
                const SR = projection.getCode().split(':')[1];
                return this.buildUrl(serviceUrl, extent, SR, opts.whereClause ? opts.whereClause : "", drawingInfo.token);
            },
            strategy: tileStrategy(createXYZ({
                tileSize: 256,
            })),
        });
        const layer = new Vector({
            opacity: opts.opacity,
            visible: opts.visible,
            source: vectorSource,
            minResolution: (_a = opts.minResolution) !== null && _a !== void 0 ? _a : 0,
            maxResolution: (_b = opts.maxResolution) !== null && _b !== void 0 ? _b : Infinity,
            style: (fl, resolution) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                if (opts.style) {
                    return new Style({
                        fill: new Fill({
                            color: (_b = (_a = opts.style) === null || _a === void 0 ? void 0 : _a.fillColor) !== null && _b !== void 0 ? _b : 'rgba(0,0,0,0)',
                        }),
                        stroke: new Stroke({
                            color: (_d = (_c = opts.style) === null || _c === void 0 ? void 0 : _c.outlineColor) !== null && _d !== void 0 ? _d : 'rgba(0,0,0,1)',
                            width: (_f = (_e = opts.style) === null || _e === void 0 ? void 0 : _e.outlineWidth) !== null && _f !== void 0 ? _f : 1.0,
                        })
                    });
                }
                if (drawingInfo.renderer.type === "simple") {
                    const style = this.styles.get(drawingInfo.renderer.type);
                    if (drawingInfo.labelingInfo && drawingInfo.labelingInfo.length > 0) {
                        const labelInfo = (_h = (_g = drawingInfo.labelingInfo) === null || _g === void 0 ? void 0 : _g.find(info => 'labelExpressionInfo' in info)) !== null && _h !== void 0 ? _h : (_j = drawingInfo.labelingInfo) === null || _j === void 0 ? void 0 : _j.find(info => 'labelExpression' in info);
                        if (labelInfo) {
                            let display_label = '';
                            if (labelInfo.labelExpressionInfo) {
                                const match = labelInfo.labelExpressionInfo.expression.match(/^\$feature\.(.+)$/);
                                display_label = match ? match[1].trim() : '';
                            }
                            else {
                                const match = labelInfo.labelExpression.match(/^\[(.+?)\]$/);
                                ;
                                display_label = match ? match[1].trim() : '';
                            }
                            let styledText;
                            if (opts.labelDisplayMinResolution != null) {
                                styledText = resolution < opts.labelDisplayMinResolution
                                    ? this.getTextSymbolStyle(labelInfo.symbol, labelInfo.allowOverrun, fl.get(display_label))
                                    : new Text();
                            }
                            else {
                                styledText = this.getTextSymbolStyle(labelInfo.symbol, labelInfo.allowOverrun, fl.get(display_label));
                            }
                            style.setText(styledText);
                        }
                        else {
                            console.warn("No 'labelExpressionInfo' or 'labelExpression' object found in labelingInfo.");
                        }
                        return style;
                    }
                    else {
                        let display_key = opts === null || opts === void 0 ? void 0 : opts.displayLabelKey;
                        if (!display_key)
                            return style;
                        display_key = display_key.trim();
                        if (fl.get(display_key)) {
                            const label_style = opts === null || opts === void 0 ? void 0 : opts.displayLabelStyle;
                            const placement = opts === null || opts === void 0 ? void 0 : opts.displayLabelPlacement;
                            style.setText(this.createTextStyle(fl.get(display_key), label_style, placement));
                        }
                        return style;
                    }
                }
                else {
                    const label = fl.get(drawingInfo.renderer.field1);
                    const style = (_k = this.styles.get(label)) !== null && _k !== void 0 ? _k : new Style();
                    let display_key = opts === null || opts === void 0 ? void 0 : opts.displayLabelKey;
                    if (!display_key)
                        return style;
                    display_key = display_key.trim();
                    if (fl.get(display_key)) {
                        const label_style = opts === null || opts === void 0 ? void 0 : opts.displayLabelStyle;
                        const placement = opts === null || opts === void 0 ? void 0 : opts.displayLabelPlacement;
                        style.setText(this.createTextStyle(fl.get(display_key), label_style, placement));
                    }
                    return style;
                }
            },
        });
        layer.set('name', opts.name);
        this.raw = new Some(layer);
        this.legend_ui = this.create_legend_ui();
        (_c = layer.getSource()) === null || _c === void 0 ? void 0 : _c.on("featuresloadstart", () => {
            this.display_loading_indicator(true);
        });
        (_d = layer.getSource()) === null || _d === void 0 ? void 0 : _d.on("featuresloadend", () => {
            this.display_loading_indicator(false);
        });
    }
    buildUrl(serviceUrl, extent, SR, whereClause, token) {
        let params = {
            f: "json",
            returnGeometry: true,
            spatialRel: "esriSpatialRelIntersects",
            geometry: `{ "xmin" : ${extent[0]}, "ymin" : ${extent[1]}, "xmax" : ${extent[2]}, "ymax": ${extent[3]}, "spatialReference" : { "wkid": ${SR} } }`,
            geometryType: "esriGeometryEnvelope",
            inSR: SR,
            outFields: "*",
            outSR: SR,
            resultType: "tile",
            where: whereClause,
            geometryPrecision: 4,
        };
        if (token) {
            params.token = token;
        }
        const serialize = (obj) => {
            let strs = [];
            for (let p in obj) {
                if (obj.hasOwnProperty(p)) {
                    strs.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
                }
            }
            return strs.join('&');
        };
        return `${serviceUrl}/query?${serialize(params)}`;
    }
    extract_styles(drawingInfo, title = "") {
        var _a, _b;
        if (drawingInfo.renderer.type === "simple") {
            const symbol = drawingInfo.renderer.symbol;
            const style = this.getStyleForSymbol(symbol);
            this.styles.set(drawingInfo.renderer.type, style);
        }
        else if (drawingInfo.renderer.type === "uniqueValue") {
            let renderer_key = drawingInfo.renderer.field1;
            (_a = drawingInfo.renderer.uniqueValueInfos) === null || _a === void 0 ? void 0 : _a.map((info) => {
                const value = info.value;
                const label = info.label;
                const symbol = info.symbol;
                const style = this.getStyleForSymbol(symbol);
                this.styles.set(value, style);
                if (value !== label) {
                    this.styles.set(label, style);
                }
                let parsed = this.parse_style(style);
                if (parsed instanceof Icon) {
                    this.create_legend_entry(label, parsed);
                }
                else {
                    this.create_legend_entry(label, parsed);
                }
            });
            let defaultSymbol = drawingInfo.renderer.defaultSymbol;
            let defaultLabel = (_b = drawingInfo.renderer.defaultLabel) !== null && _b !== void 0 ? _b : "default label";
            if (defaultSymbol) {
                const style = this.getStyleForSymbol(defaultSymbol);
                this.styles.set(defaultLabel, style);
                let parsed = this.parse_style(style);
                if (parsed instanceof Icon) {
                    this.create_legend_entry(defaultLabel, parsed);
                }
                else {
                    this.create_legend_entry(defaultLabel, parsed);
                }
            }
        }
        else {
            throw new Error(`Unsupported renderer type: ${drawingInfo.renderer.type}`);
        }
    }
    get visible() {
        return super.visible;
    }
    set visible(val) {
        this.legend_ui.toggleClass('hidden', !val);
        if (val) {
            if (!this.legend_entries.length) {
                this.legend_ui.toggleClass('hidden', true);
            }
            else {
                if (this.legend_entries.length <= 20) {
                    this.legend_ui.append(this.legend_entries);
                }
            }
        }
        super.visible = val;
    }
    create_legend_ui() {
        return $(`<div data-fs-layer-legend-ref="${this.layer_id}" class="fs-layer-legend hidden"></div>`);
    }
    parse_style(style) {
        if (style.getImage()) {
            if (style.getImage() instanceof Icon) {
                return style.getImage();
            }
            else {
                if (style.getImage() instanceof CircleStyle) {
                    let image = style.getImage();
                    return image.getFill().getColor();
                }
                else {
                    let image = style.getImage();
                    return image.getFill().getColor();
                }
            }
        }
        else {
            try {
                return style.getFill().getColor();
            }
            catch (_a) {
                return style.getStroke().getColor();
            }
        }
    }
    create_legend_entry(title, item) {
        let entry = $(`<div class="legend"></div>`);
        let label_elm = this.generate_label(title);
        if (item instanceof Icon) {
            let img_elm = this.generate_img(item);
            label_elm.prepend(img_elm);
        }
        else {
            let color_string = this.getRGBAStringFromColor(item);
            let color_elm = this.generate_color(color_string);
            label_elm.prepend(color_elm);
        }
        entry.append(label_elm);
        this.legend_entries.push(entry);
    }
    generate_label(text) {
        let label_span = $(`<label></label>`);
        label_span.attr("id", text);
        label_span.text(text);
        return label_span;
    }
    generate_color(color) {
        let color_span = $(`<span></span>`);
        color_span.addClass("color");
        color_span.attr("style", `background-color:${color} !important;`);
        return color_span;
    }
    generate_img(icon) {
        icon.load();
        let img = $(icon.getImage(1));
        img.addClass("icon-img");
        return img;
    }
    getRGBAStringFromColor(color) {
        let c = this.esriToOlColor(color);
        return `rgba(${c[0]},${c[1]},${c[2]},${c[3]})`;
    }
    esriToOlColor(color) {
        let r = color[0];
        let g = color[1];
        let b = color[2];
        let a = color[3] > 1 ? (color[3] / 255) : color[3];
        return [r, g, b, a];
    }
    getStyleForSymbol(s) {
        let symbol;
        let style = new Style({
            fill: new Fill({
                color: `rgba(0,0,0,1)`
            }),
        });
        switch (s.type) {
            case "esriSFS":
                symbol = s;
                style = this.getSFSStyle(symbol);
                break;
            case "esriPFS":
                symbol = s;
                break;
            case "esriSMS":
                symbol = s;
                style = this.getSMSStyle(symbol);
                break;
            case "esriPMS":
                symbol = s;
                style = new Style({
                    image: new Icon({
                        src: (symbol.contentType && symbol.imageData) ? `data:${symbol.contentType};base64,${symbol.imageData}` : undefined,
                        scale: [symbol.width / symbol.width, symbol.height / symbol.height],
                        rotation: symbol.angle * Math.PI / 180,
                        anchor: [0.5 - symbol.xoffset / symbol.width, 0.5 + symbol.yoffset / symbol.height]
                    })
                });
                break;
            case "esriSLS":
                symbol = s;
                style = this.getSLSStyle(symbol);
                break;
            default:
                break;
        }
        return style;
    }
    getSMSStyle(symbol) {
        if (symbol.style === "esriSMSCross") {
            return new Style({
                image: new RegularShape({
                    points: 4,
                    radius: symbol.size / 2,
                    radius2: 0,
                    angle: symbol.angle * Math.PI / 180,
                    displacement: [symbol.xoffset, -symbol.yoffset],
                    stroke: symbol.outline ? new Stroke({
                        color: this.esriToOlColor(symbol.outline.color),
                        width: symbol.outline.width
                    }) : undefined,
                    fill: new Fill({
                        color: symbol.color ? this.esriToOlColor(symbol.color) : undefined
                    })
                })
            });
        }
        else if (symbol.style === "esriSMSDiamond") {
            return new Style({
                image: new RegularShape({
                    points: 4,
                    radius: symbol.size / 2,
                    angle: symbol.angle * Math.PI / 180,
                    displacement: [symbol.xoffset, -symbol.yoffset],
                    stroke: symbol.outline ? new Stroke({
                        color: this.esriToOlColor(symbol.outline.color),
                        width: symbol.outline.width
                    }) : undefined,
                    fill: new Fill({
                        color: symbol.color ? this.esriToOlColor(symbol.color) : undefined
                    })
                })
            });
        }
        else if (symbol.style === "esriSMSSquare") {
            return new Style({
                image: new RegularShape({
                    points: 4,
                    radius: symbol.size / 2,
                    angle: Math.PI / 4 + symbol.angle * Math.PI / 180,
                    displacement: [symbol.xoffset, -symbol.yoffset],
                    stroke: symbol.outline ? new Stroke({
                        color: this.esriToOlColor(symbol.outline.color),
                        width: symbol.outline.width
                    }) : undefined,
                    fill: new Fill({
                        color: symbol.color ? this.esriToOlColor(symbol.color) : undefined
                    })
                })
            });
        }
        else if (symbol.style === "esriSMSTriangle") {
            return new Style({
                image: new RegularShape({
                    points: 3,
                    radius: symbol.size / 2,
                    angle: symbol.angle * Math.PI / 180,
                    displacement: [symbol.xoffset, -symbol.yoffset],
                    stroke: symbol.outline ? new Stroke({
                        color: this.esriToOlColor(symbol.outline.color),
                        width: symbol.outline.width
                    }) : undefined,
                    fill: new Fill({
                        color: symbol.color ? this.esriToOlColor(symbol.color) : undefined
                    })
                })
            });
        }
        else if (symbol.style === "esriSMSX") {
            return new Style({
                image: new RegularShape({
                    points: 4,
                    radius: symbol.size / 2,
                    radius2: 0,
                    angle: Math.PI / 4 + symbol.angle * Math.PI / 180,
                    displacement: [symbol.xoffset, -symbol.yoffset],
                    stroke: symbol.outline ? new Stroke({
                        color: this.esriToOlColor(symbol.outline.color),
                        width: symbol.outline.width
                    }) : undefined,
                    fill: new Fill({
                        color: symbol.color ? this.esriToOlColor(symbol.color) : undefined
                    })
                })
            });
        }
        else {
            return new Style({
                image: new CircleStyle({
                    radius: symbol.size,
                    fill: new Fill({
                        color: this.esriToOlColor(symbol.color)
                    }),
                    stroke: symbol.outline ? new Stroke({
                        color: this.esriToOlColor(symbol.outline.color),
                        width: symbol.outline.width
                    }) : undefined,
                })
            });
        }
    }
    getSLSStyle(symbol) {
        return new Style({
            stroke: new Stroke({
                color: symbol.color ? this.esriToOlColor(symbol.color) : undefined,
                width: symbol.width ? symbol.width : undefined,
                lineDash: symbol.style ? this.getSLSPattern(symbol.style) : undefined
            })
        });
    }
    getSFSStyle(symbol) {
        return new Style({
            fill: symbol.color ? new Fill({
                color: this.esriToOlColor(symbol.color),
            }) : undefined,
            stroke: symbol.outline ? new Stroke({
                color: this.esriToOlColor(symbol.outline.color),
                width: symbol.outline.width,
                lineDash: this.getSLSPattern(symbol.outline.style)
            }) : undefined
        });
    }
    getSLSPattern(style) {
        switch (style) {
            case "esriSLSDash":
                return [10, 10];
            case "esriSLSDashDot":
                return [10, 5, 1, 5];
            case "esriSLSDashDotDot":
                return [10, 5, 1, 5, 1, 5];
            case "esriSLSDot":
                return [1, 10];
            case "esriSLSLongDash":
                return [20, 10];
            case "esriSLSLongDashDot":
                return [20, 5, 1, 5];
            case "esriSLSShortDash":
                return [5, 5];
            case "esriSLSShortDashDot":
                return [5, 2.5, 1, 2.5];
            case "esriSLSShortDashDotDot":
                return [5, 2.5, 1, 2.5, 1, 2.5];
            case "esriSLSShortDot":
                return [1, 5];
            default:
                return undefined;
        }
    }
    getTextSymbolStyle(symbol, allowOverrun = true, display_label) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        const fontSize = (_b = (_a = symbol.font) === null || _a === void 0 ? void 0 : _a.size) !== null && _b !== void 0 ? _b : 12;
        const fontFamily = (_d = (_c = symbol.font) === null || _c === void 0 ? void 0 : _c.family) !== null && _d !== void 0 ? _d : 'Arial';
        const fontStyle = (_f = (_e = symbol.font) === null || _e === void 0 ? void 0 : _e.style) !== null && _f !== void 0 ? _f : 'normal';
        const fontWeight = (_h = (_g = symbol.font) === null || _g === void 0 ? void 0 : _g.weight) !== null && _h !== void 0 ? _h : 'normal';
        const color = this.esriToOlColor((_j = symbol.color) !== null && _j !== void 0 ? _j : [0, 0, 0, 255]);
        const textAlign = ['left', 'right', 'center', 'start', 'end'].includes(symbol.horizontalAlignment)
            ? symbol.horizontalAlignment
            : 'center';
        const textBaseline = ['top', 'middle', 'bottom', 'alphabetic', 'ideographic', 'hanging'].includes(symbol.verticalAlignment)
            ? symbol.verticalAlignment
            : 'middle';
        if (!display_label) {
            return new Text();
        }
        else {
            if (typeof (display_label) === 'number')
                display_label = display_label.toString();
        }
        return new Text({
            font: `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}, Arial, sans-serif`,
            text: display_label.trim(),
            fill: new Fill({ color }),
            offsetX: (_k = symbol.xoffset) !== null && _k !== void 0 ? _k : 0,
            offsetY: (_l = symbol.yoffset) !== null && _l !== void 0 ? _l : 0,
            rotation: ((_m = symbol.angle) !== null && _m !== void 0 ? _m : 0) * Math.PI / 180,
            textAlign,
            textBaseline,
            overflow: allowOverrun,
            stroke: symbol.haloColor
                ? new Stroke({
                    color: this.esriToOlColor(symbol.haloColor),
                    width: Number((_o = symbol.haloSize) !== null && _o !== void 0 ? _o : 1)
                })
                : undefined
        });
    }
    async downloadKml() {
        var _a, _b;
        if (this.raw.isNone())
            return;
        this.display_loading_indicator(true);
        try {
            await ((_a = this.map) === null || _a === void 0 ? void 0 : _a.download_complete_layer_kml(this.name, (_b = this.opts) === null || _b === void 0 ? void 0 : _b.exportUrl[0]));
        }
        catch (error) {
            console.error(`unable to download ${this.name} kml data. ${error}`);
        }
        this.display_loading_indicator(false);
    }
    createTextStyle(label, style, placement = 'line') {
        var _a, _b, _c, _d, _e, _f;
        return new Text({
            text: label,
            font: (_a = style === null || style === void 0 ? void 0 : style.font) !== null && _a !== void 0 ? _a : '12px Arial',
            fill: new Fill({
                color: (_b = style === null || style === void 0 ? void 0 : style.fillColor) !== null && _b !== void 0 ? _b : '#000',
            }),
            stroke: new Stroke({
                color: (_c = style === null || style === void 0 ? void 0 : style.outlineColor) !== null && _c !== void 0 ? _c : '#fff',
                width: (_d = style === null || style === void 0 ? void 0 : style.outlineWidth) !== null && _d !== void 0 ? _d : 2,
            }),
            placement: placement === 'line' ? TextPlacement.LINE : TextPlacement.POINT,
            maxAngle: Math.PI / 4,
            overflow: placement === 'line' ? false : true,
            offsetX: (_e = style === null || style === void 0 ? void 0 : style.offsetX) !== null && _e !== void 0 ? _e : 0,
            offsetY: (_f = style === null || style === void 0 ? void 0 : style.offsetY) !== null && _f !== void 0 ? _f : 0
        });
    }
}
